"use client";

import React, { useState, useEffect, useRef } from "react";
import { clsx } from "clsx";
import AdaptiveNav from "@/components/AdaptiveNav";
import AppHeader from "@/components/AppHeader";
import { GuestNav } from "@/components/GuestNav";
import { GuestHeader } from "@/components/GuestHeader";
import { GuestMascotGreeter } from "@/components/GuestMascotGreeter";
import { usePathname, useRouter } from "next/navigation";
import { useAuth } from "@/lib/AuthContext";
import { apiFetch } from "@/lib/api";
import { publicRoutes, publicPrefixes } from "@/components/shared/routes";
import { isStandaloneRoute } from "@/components/shared/standaloneRoutes";
import ContentProtection from "@/components/ContentProtection";
import ScreenshotShield from "@/components/ScreenshotShield";
import OfflineBanner from "@/components/OfflineBanner";
import RegisterSW from "@/components/RegisterSW";
import { useScreenshotGuard } from "@/hooks/useIOSScreenshotGuard";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import { X } from 'lucide-react';

function isCrawler(userAgent: string | null): boolean {
  if (!userAgent) return false;
  const ua = userAgent.toLowerCase();
  return (
    ua.includes('googlebot') ||
    ua.includes('bingbot') ||
    ua.includes('slurp') ||
    ua.includes('duckduckbot') ||
    ua.includes('baiduspider') ||
    ua.includes('yandexbot') ||
    ua.includes('facebookexternalhit') ||
    ua.includes('twitterbot') ||
    ua.includes('rogerbot') ||
    ua.includes('linkedinbot') ||
    ua.includes('crawl') ||
    ua.includes('spider')
  );
}

interface LayoutWrapperProps {
  children: React.ReactNode;
}

export default function LayoutWrapper({ children }: LayoutWrapperProps) {
  const pathname = usePathname();
  const router = useRouter();
  const { user, loading, role } = useAuth();
  const [windowWidth, setWindowWidth] = useState<number>(
    typeof window !== "undefined" ? window.innerWidth : 0,
  );
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [guestCollapsed, setGuestCollapsed] = useState(false);
  const [adaptiveBottomBarVisible, setAdaptiveBottomBarVisible] =
    useState(true);
  const [checksDone, setChecksDone] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [needsTerms, setNeedsTerms] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const checkedPathRef = useRef<string | null>(null);
  const crawlerUserAgent =
    typeof window !== 'undefined' ? navigator.userAgent : null;
  const isBot = isCrawler(crawlerUserAgent);

  const isPublicRoute =
    publicRoutes.includes(pathname) ||
    publicPrefixes.some((p) => pathname.startsWith(p));

  const isAskRoute = pathname.startsWith("/ask");

  // Sync sidebar state
  useEffect(() => {
    const handleSidebarToggle = (e: Event) =>
      setIsSidebarCollapsed((e as CustomEvent).detail);
    window.addEventListener("sidebarToggle", handleSidebarToggle);
    return () =>
      window.removeEventListener("sidebarToggle", handleSidebarToggle);
  }, []);

  // Sync adaptive mobile bottom bar visibility
  useEffect(() => {
    const handleAdaptiveBar = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail && typeof detail.visible === "boolean") {
        setAdaptiveBottomBarVisible(detail.visible);
      }
    };
    window.addEventListener(
      "adaptiveBottomBarChange",
      handleAdaptiveBar as EventListener,
    );
    return () =>
      window.removeEventListener(
        "adaptiveBottomBarChange",
        handleAdaptiveBar as EventListener,
      );
  }, []);

  // Auth Guard + Onboarding/Terms gate
  useEffect(() => {
    if (loading) return;
    if (isStandaloneRoute(pathname)) return;
    if (pathname.startsWith("/admin")) return;

    const isAuthOrOnboarding =
      pathname.startsWith("/auth/") ||
      pathname === "/onboarding" ||
      pathname === "/onboarding/courses";

    if (!user && !isPublicRoute) {
      router.replace(`/auth/login?redirect=${encodeURIComponent(pathname)}`);
      return;
    }
    if (user && (pathname === "/auth/login" || pathname === "/auth/signup")) {
      if (role === "course_rep") router.replace("/course-rep");
      else router.replace("/dashboard");
      return;
    }
    if (user && pathname === "/" && !isBot) {
      if (role === "course_rep") router.replace("/course-rep");
      else router.replace("/dashboard");
      return;
    }
    if (
      user &&
      role === "course_rep" &&
      !pathname.startsWith("/course-rep") &&
      !isPublicRoute
    ) {
      router.replace("/course-rep");
      return;
    }
    if (
      user &&
      !isAuthOrOnboarding &&
      !isStandaloneRoute(pathname) &&
      !pathname.startsWith("/admin")
    ) {
      if (checkedPathRef.current !== pathname) return;
      if (needsOnboarding && !pathname.startsWith("/onboarding")) {
        router.replace("/onboarding");
        return;
      }
      if (needsTerms) {
        router.replace("/terms/accept");
      }
    }
  }, [
    user,
    loading,
    pathname,
    router,
    isPublicRoute,
    role,
    checksDone,
    needsOnboarding,
    needsTerms,
  ]);

  // Fetch profile once to check onboarding + terms status
  useEffect(() => {
    if (loading || !user || isStandaloneRoute(pathname)) return;
    const isAuthOrOnboarding =
      pathname.startsWith("/auth/") ||
      pathname === "/onboarding" ||
      pathname === "/onboarding/courses" ||
      pathname === "/terms" ||
      pathname === "/terms/accept" ||
      pathname === "/privacy";
    if (isAuthOrOnboarding) {
      setChecksDone(true);
      checkedPathRef.current = pathname;
      return;
    }

    const checkProfile = async () => {
      try {
        const profile = await apiFetch<Record<string, unknown>>(
          "/api/v1/user/profile",
        );
        const onboardingCompleted =
          (profile as any)?.onboarding_completed === true;
        const profileComplete = (profile as any)?.profile_complete === true;
        const termsAccepted =
          (profile as any)?.terms_accepted_at !== undefined &&
          (profile as any)?.terms_accepted_at !== null;
        setNeedsOnboarding(!profileComplete && !onboardingCompleted);
        setNeedsTerms(!termsAccepted);
      } catch {
        setNeedsOnboarding(false);
        setNeedsTerms(false);
      } finally {
        setChecksDone(true);
        checkedPathRef.current = pathname;
      }
    };
    checkProfile();
  }, [loading, user, pathname]);

  // Window Width Sync
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isTabletOrMobile = windowWidth > 0 && windowWidth < 1024;

  useEffect(() => {
    document.documentElement.style.setProperty("--header-height", "72px");
    if (isTabletOrMobile) {
      document.documentElement.style.setProperty("--nav-width", "0px");
    } else {
      document.documentElement.style.setProperty(
        "--nav-width",
        isSidebarCollapsed ? "80px" : "256px",
      );
    }
  }, [windowWidth, isSidebarCollapsed, isTabletOrMobile]);

  const { showOverlay } = useScreenshotGuard({
    userName: user?.preferredName || user?.name || null,
  });

  if (loading && !isPublicRoute) {
    return (
      <div className="min-h-screen bg-background text-foreground noise-texture flex flex-col">
        <OfflineBanner />
        <div className="flex-1 w-full">{children}</div>
      </div>
    );
  }

  const isAdminRoute = pathname.startsWith("/admin");

  if (isAdminRoute) {
    return (
      <div className="min-h-screen bg-background text-foreground flex flex-col noise-texture relative">
        <OfflineBanner />
        <div className="flex-1 w-full">{children}</div>
      </div>
    );
  }

  const showAdaptiveNav = !!user && !isAdminRoute;
  const showAppHeader =
    !!user &&
    !isAdminRoute &&
    !pathname.startsWith("/course-rep") &&
    (pathname === "/dashboard" ||
      pathname.startsWith('/ask') ||
      pathname.startsWith("/settings") ||
      pathname.startsWith("/profile") ||
      pathname.startsWith("/courses") ||
      pathname.startsWith("/community") ||
      pathname.startsWith("/learn") ||
      pathname.startsWith("/events") ||
      pathname.startsWith("/tools"));

  const isProtectedContent =
    !!user && !isStandaloneRoute(pathname) && !isAdminRoute;

  const isAuthRoute = pathname.startsWith("/auth/");

  if (isPublicRoute && !showAdaptiveNav) {
    return (
      <div className="min-h-screen bg-background text-foreground noise-texture flex flex-col">
        <OfflineBanner />
        {!isAuthRoute && (
          <GuestNav
            collapsed={guestCollapsed}
            onToggle={() => setGuestCollapsed((c) => !c)}
          />
        )}
        <div
          className={clsx(
            "flex min-h-screen flex-col transition-[padding] duration-300 ease-spring flex-1",
            isAuthRoute ? "" : "lg:pl-16",
            isAuthRoute ? "" : guestCollapsed ? "xl:pl-16" : "xl:pl-64",
          )}
        >
          {!isAuthRoute && <GuestHeader />}
          <div className="flex-1 w-full">{children}</div>
          {!isAuthRoute && <GuestMascotGreeter navCollapsed={guestCollapsed} />}
        </div>
      </div>
    );
  }

  return (
    <ContentProtection>
      <div className="h-screen flex flex-col bg-background text-foreground transition-colors duration-300 overflow-hidden relative">
        <OfflineBanner />

        <div
          className="flex-1 flex overflow-hidden relative overscroll-contain"
          style={{ paddingLeft: isTabletOrMobile ? "0" : "var(--nav-width)" }}
        >
          {/* Desktop sidebar */}
          {showAdaptiveNav && !isTabletOrMobile && <AdaptiveNav />}
          
          {/* Mobile nav overlay */}
          {showAdaptiveNav && isTabletOrMobile && mobileNavOpen && (
            <>
              <div 
                className="fixed inset-0 bg-black/60 z-[150]" 
                onClick={() => setMobileNavOpen(false)} 
              />
              <div className="fixed top-0 left-0 bottom-0 w-72 max-w-[80vw] h-screen z-[160] bg-card border-r border-border flex flex-col noise-texture shadow-2xl">
                <div className="h-14 flex items-center justify-between px-4 border-b border-border">
                  <span className="text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--foreground)' }}>Menu</span>
                  <button onClick={() => setMobileNavOpen(false)} className="p-2 rounded-lg bg-secondary text-muted-foreground hover:text-foreground transition-colors" aria-label="Close navigation">
                    <X size={18} />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto">
                  <AdaptiveNav />
                </div>
              </div>
            </>
          )}

          <main
            className="flex-1 min-w-0 flex flex-col h-full min-h-0 transition-all duration-300 ease-spring overflow-hidden"
            style={{
              paddingBottom: isTabletOrMobile && !isAskRoute
                ? adaptiveBottomBarVisible
                  ? "calc(68px + env(safe-area-inset-bottom, 0px))"
                  : "0px"
                : "0px",
            }}
          >
            {showAppHeader && (
              <AppHeader 
                onToggleMobileNav={() => setMobileNavOpen((prev) => !prev)}
                mobileNavOpen={mobileNavOpen}
              />
            )}
            <div
              className={clsx(
                "flex-1 overflow-y-auto w-full mx-auto px-4 sm:px-5 lg:px-6 pb-6 pt-6 max-w-[1600px] noise-texture relative z-10 overscroll-contain",
                showAppHeader ? "pt-[var(--header-height)]" : "pt-6",
              )}
            >
              {children}
            </div>
          </main>
        </div>
      </div>
      <ScreenshotShield visible={showOverlay} />
    </ContentProtection>
  );
}
